import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { finalizeSlug, isValidSlug } from '@photo-merge/shared';
import { BallMark } from '../components/BallMark';
import { Button } from '../components/Button';
import { StockFace } from '../components/StockFace';
import styles from './Home.module.css';

function Chevron() {
  return (
    <svg
      className={styles.chevron}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** Stacked photos with a mountain-and-sun frame — reads as "pick from your camera roll". */
function PhotoStackIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="6.5"
        y="2.5"
        width="15"
        height="15"
        rx="3.2"
        stroke="currentColor"
        strokeWidth="1.9"
        opacity="0.45"
      />
      <rect
        x="2.5"
        y="6.5"
        width="15"
        height="15"
        rx="3.2"
        fill="currentColor"
        fillOpacity="0.14"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <circle cx="7.4" cy="11.2" r="1.5" fill="currentColor" />
      <path
        d="M3.4 18.2l3.9-4a1.5 1.5 0 0 1 2.1 0l2.5 2.6 1.6-1.4a1.5 1.5 0 0 1 2 0l2 1.8"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface PathCardProps {
  to: string;
  icon: React.ReactNode;
  tint: string;
  title: string;
  hint: string;
}

function PathCard({ to, icon, tint, title, hint }: PathCardProps) {
  return (
    <Link to={to} className={styles.card}>
      <span className={styles.cardIcon} style={{ background: tint }} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.cardBody}>
        <span className={styles.cardTitle}>{title}</span>
        <span className={styles.cardHint}>{hint}</span>
      </span>
      <Chevron />
    </Link>
  );
}

export function Home() {
  const navigate = useNavigate();
  const [entered, setEntered] = useState('');
  const [error, setError] = useState<string>();

  const slug = finalizeSlug(entered);

  function join() {
    if (!isValidSlug(slug)) {
      setError('That doesn’t look like a game link.');
      return;
    }
    setError(undefined);
    navigate(`/play/${slug}`);
  }

  return (
    <main className="page">
      <header className={styles.hero}>
        <BallMark size={128} className={styles.mark} />
        <h1 className={styles.title}>
          MergeBalls<span className={styles.tld}>.com</span>
        </h1>
        <p className={styles.tagline}>Turn your photos into balls. Drop them. Merge them.</p>
      </header>

      {/* Sits above the two paths: arriving with a link in hand is the most
          time-sensitive case, and shouldn't require scrolling past them. */}
      <section className={styles.join}>
        <label className={styles.joinLabel} htmlFor="game-link">
          Got a game link?
        </label>
        <form
          className={styles.joinRow}
          onSubmit={(e) => {
            e.preventDefault();
            join();
          }}
        >
          {/* Outside the input, so it reads as a fixed prefix rather than placeholder
              text you're meant to overwrite. */}
          <span className={styles.joinPrefix} aria-hidden="true">
            /play/
          </span>
          <input
            id="game-link"
            className={styles.joinInput}
            value={entered}
            onChange={(e) => {
              setEntered(e.target.value);
              if (error) setError(undefined);
            }}
            placeholder="grandmas-80th"
            aria-label="Game link"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="go"
          />
          <Button type="submit" disabled={!isValidSlug(slug)}>
            Go
          </Button>
        </form>
        {error ? (
          <p className={styles.joinError} role="alert">
            {error}
          </p>
        ) : null}
      </section>

      <div className={styles.cards}>
        <PathCard
          to="/create"
          icon={<PhotoStackIcon />}
          tint="var(--tint-warm)"
          title="Create a game"
          hint="Pick photos from your camera roll"
        />
        <PathCard
          to="/random"
          icon={<StockFace size={34} />}
          tint="var(--tint-cool)"
          title="Quick play"
          hint="Jump in with a random cast"
        />
      </div>
    </main>
  );
}
